// Catálogo de itens e desenho dos sprites de itens a partir das folhas originais.
import type { ItemType } from '../../../shared/src/game/items';
import { stackSpriteIndex } from '../../../shared/src/game/items';

export interface ClientItemType extends ItemType { sprites: number[]; pw: number; ph: number }
interface Sheet { sheet: string; first: number; last: number; w: number; h: number; cols: number }

let catalogP: Promise<Map<number, ClientItemType>> | null = null;
let sheetsP: Promise<Sheet[]> | null = null;
let catalogSync: Map<number, ClientItemType> | null = null;

export function loadCatalog(): Promise<Map<number, ClientItemType>> {
  catalogP ??= fetch('/data/items.json', { cache: 'no-cache' }).then((r) => r.json()).then((raw: Record<string, ClientItemType>) => {
    catalogSync = new Map(Object.values(raw).map((t) => [t.id, t]));
    return catalogSync;
  });
  return catalogP;
}
/** Catálogo já carregado (ou null enquanto carrega). */
export const catalogNow = () => catalogSync;

export function loadSheets(): Promise<Sheet[]> {
  // folhas do cliente oficial + folhas dos itens custom do jogo (Set Noxara), que ficam no próprio site
  sheetsP ??= Promise.all([
    fetch('/assets/sheets-index.json').then((r) => r.json()),
    fetch('/custom/sheets.json', { cache: 'no-cache' }).then((r) => (r.ok ? r.json() : [])).catch(() => []),
  ]).then(([a, b]: Sheet[][]) => [...a!, ...b!].sort((x, y) => x.first - y.first));
  return sheetsP;
}

function findSheet(sheets: Sheet[], id: number): Sheet | undefined {
  let lo = 0, hi = sheets.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1, s = sheets[mid]!;
    if (id < s.first) hi = mid - 1; else if (id > s.last) lo = mid + 1; else return s;
  }
  return undefined;
}

const sheetImages = new Map<string, Promise<HTMLImageElement>>();
export function sheetImage(file: string): Promise<HTMLImageElement> {
  let p = sheetImages.get(file);
  if (!p) {
    p = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('folha não encontrada: ' + file));
      img.src = file.startsWith('custom/') ? '/' + file : '/assets/sheets/' + file;
    });
    sheetImages.set(file, p);
  }
  return p;
}

/** Desenha um sprite qualquer (por id) num canvas do tamanho original dele. */
export async function spriteCanvas(spriteId: number): Promise<HTMLCanvasElement | null> {
  const sheets = await loadSheets();
  const s = findSheet(sheets, spriteId);
  if (!s) return null;
  const img = await sheetImage(s.sheet);
  const i = spriteId - s.first;
  const c = document.createElement('canvas');
  c.width = s.w; c.height = s.h;
  c.getContext('2d')!.drawImage(img, (i % s.cols) * s.w, Math.floor(i / s.cols) * s.h, s.w, s.h, 0, 0, s.w, s.h);
  return c;
}

const itemCache = new Map<string, Promise<string>>();

/** Imagem (data URL, 32x32) de um item, considerando a quantidade para moedas e similares. */
export function itemImage(typeId: number, count = 1): Promise<string> {
  const key = typeId + ':' + count;
  let p = itemCache.get(key);
  if (!p) {
    p = (async () => {
      const t = (await loadCatalog()).get(typeId);
      if (!t) return '';
      const variants = t.stackable ? t.pw * t.ph : 1;
      const idx = t.stackable && variants > 1 ? stackSpriteIndex(count, variants) : 0;
      const src = await spriteCanvas(t.sprites[idx] ?? t.sprites[0]!);
      if (!src) return '';
      if (src.width === 32) return src.toDataURL();
      // Sprites maiores (64x64) são reduzidos para caber no slot, como no cliente.
      const c = document.createElement('canvas');
      c.width = c.height = 32;
      c.getContext('2d')!.drawImage(src, 0, 0, 32, 32);
      return c.toDataURL();
    })();
    itemCache.set(key, p);
  }
  return p;
}
